"use client";

import { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { Upload, Sparkles, Check, X, MapPin, Camera, Mic } from 'lucide-react';
import { db, storage } from '@/lib/firebase/client';
import { addDoc, collection, doc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import VoiceAssistant from '@/components/ai/VoiceAssistant';
import { useVoiceAssistantEnabled } from '@/hooks/useVoiceAssistantEnabled';
import { useAuth } from '@/contexts/AuthContext';
import { CATEGORIES } from '@/lib/categories';

export default function TenderPage() {
  const router = useRouter();
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);

  const [category, setCategory] = useState('');
  const [urgency, setUrgency] = useState<'urgent' | 'today' | 'flexible'>('flexible');
  const [description, setDescription] = useState('');
  const [budget, setBudget] = useState('');
  const [city, setCity] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  // The error renders at the TOP of a long form while the button sits at the
  // bottom: without scrolling to it, pressing "send" on an incomplete form looked
  // like nothing happened at all. Category is the field that gets here — the
  // others are native inputs the browser itself refuses to submit.
  const errorRef = useRef<HTMLDivElement>(null);
  // Bumped on every failed attempt so pressing "send" twice scrolls twice.
  const [errorTick, setErrorTick] = useState(0);
  // Non-blocking notice: the request went through but photos could not be attached.
  const [photoWarning, setPhotoWarning] = useState('');
  const [voiceOpen, setVoiceOpen] = useState(false);
  const { enabled: voiceEnabled } = useVoiceAssistantEnabled();

  // Auto-fill contact info from user profile
  useEffect(() => {
    if (!user) return;
    // Set name from Google displayName
    if (user.displayName && !clientName) {
      setClientName(user.displayName);
    }
    // Set phone from Firebase Auth phoneNumber
    if (user.phoneNumber && !clientPhone) {
      setClientPhone(user.phoneNumber);
    }
    // Try to get phone from Firestore users doc if not available from Auth
    if (!user.phoneNumber) {
      getDoc(doc(db, 'users', user.uid)).then(snap => {
        if (snap.exists()) {
          const data = snap.data();
          if (data.phone && !clientPhone) setClientPhone(data.phone);
          if (data.name && !clientName) setClientName(data.name);
        }
      }).catch(console.error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleVoiceTender = (data: { category: string; description: string; budget: string; city: string }) => {
    if (data.category) setCategory(data.category);
    if (data.description) setDescription(data.description);
    if (data.budget) setBudget(data.budget);
    if (data.city) setCity(data.city);
    setVoiceOpen(false);
  };

  const handlePhotos = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setPhotos(prev => [...prev, ...files].slice(0, 4));
  };

  const removePhoto = (i: number) => {
    setPhotos(prev => prev.filter((_, idx) => idx !== i));
  };

  useEffect(() => {
    if (!error) return;
    // NOT `behavior: 'smooth'`: with the system "reduce motion" setting on, the
    // smooth scroll is dropped entirely and the page never moves — so exactly
    // the people who need clear feedback would still see nothing happen.
    errorRef.current?.scrollIntoView({ block: 'center' });
  }, [error, errorTick]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!category || !description || !clientName || !clientPhone) {
      setError(t('tender.required_fields_error'));
      setErrorTick(n => n + 1);
      return;
    }
    setError('');
    setPhotoWarning('');
    setSubmitting(true);
    try {
      // Upload photos BEST-EFFORT: a failed upload must never lose the whole
      // request. (Storage rules require auth on tenders/, so a guest's upload
      // fails — previously that threw and the entire tender was discarded.)
      const photoUrls: string[] = [];
      let failedPhotos = 0;
      if (photos.length > 0 && storage) {
        for (const file of photos) {
          try {
            const path = `tenders/${user?.uid || 'guest'}/${Date.now()}_${file.name}`;
            const storageRef = ref(storage, path);
            await uploadBytes(storageRef, file);
            photoUrls.push(await getDownloadURL(storageRef));
          } catch (uploadErr) {
            console.error('tender photo upload failed:', uploadErr);
            failedPhotos++;
          }
        }
      }
      if (failedPhotos > 0) setPhotoWarning(t('tender.photos_failed_note'));
      await addDoc(collection(db, 'tenders'), {
        category,
        urgency,
        description,
        budget: budget ? Number(budget) : null,
        city,
        clientName,
        clientPhone,
        photos: photoUrls,
        photoUrl: photoUrls[0] || '',
        status: 'open',
        userId: user?.uid || null,
        createdAt: new Date()
      });
      setSuccess(true);
    } catch (err) {
      console.error(err);
      setError(t('tender.send_error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center container mx-auto px-4 text-center">
        <div className="w-28 h-28 bg-linear-to-br from-green-400 to-emerald-600 rounded-full flex items-center justify-center mb-6 shadow-2xl shadow-green-500/30">
          <Check className="w-14 h-14 text-white" />
        </div>
        <h1 className="text-4xl font-black mb-3">{t('tender.success_title')}</h1>
        <p className="text-xl text-muted-foreground mb-4 max-w-md">{t('tender.success_text')}</p>
        {photoWarning && (
          <p className="text-sm font-medium text-amber-600 bg-amber-500/10 border border-amber-500/20 rounded-2xl px-4 py-3 mb-6 max-w-md">
            {photoWarning}
          </p>
        )}
        <div className="mb-4" />
        <div className="flex gap-4">
          <button onClick={() => router.push('/catalog')}
            className="bg-primary text-primary-foreground px-8 py-4 rounded-full font-bold hover:scale-105 transition-transform shadow-lg shadow-primary/20">
            {t('tender.see_masters')}
          </button>
          <button onClick={() => {
            setSuccess(false); setCategory(''); setDescription('');
            setBudget(''); setClientName(''); setClientPhone(''); setPhotos([]); setUrgency('flexible');
          }} className="bg-card border border-border px-8 py-4 rounded-full font-bold hover:bg-secondary transition-colors">
            {t('tender.new_request')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="bg-linear-to-b from-primary/8 to-background py-14 px-4 border-b border-border mb-10">
        <div className="container mx-auto max-w-2xl text-center">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-bold mb-4">
            <Sparkles className="w-4 h-4" /> {t('tender.badge')}
          </div>
          <h1 className="text-5xl font-black mb-4">{t('tender.title')}</h1>
          <p className="text-xl text-muted-foreground font-medium max-w-lg mx-auto">{t('tender.subtitle')}</p>
        </div>
      </div>

      <div className="container mx-auto max-w-2xl px-4 pb-24">
        <form onSubmit={handleSubmit} className="space-y-8">

          {error && (
            <div ref={errorRef} className="p-4 bg-red-500/10 border border-red-500/20 text-red-500 rounded-2xl font-medium text-sm flex items-center gap-2">
              <X className="w-4 h-4 shrink-0" /> {error}
            </div>
          )}

          {/* Category grid */}
          <div>
            <label className="text-sm font-black uppercase tracking-wider text-muted-foreground block mb-4">
              {t('tender.category_label')}
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              {CATEGORIES.map(cat => (
                <button
                  type="button"
                  key={cat.key}
                  onClick={() => setCategory(cat.key)}
                  className={`py-4 px-3 rounded-2xl border-2 font-bold text-sm text-center transition-all flex flex-col items-center gap-2 ${
                    category === cat.key
                      ? 'border-primary bg-primary/10 text-primary shadow-lg shadow-primary/10'
                      : 'border-border bg-card hover:border-primary/40 hover:bg-secondary'
                  }`}
                >
                  <span className="text-2xl">{cat.emoji}</span>
                  <span className="text-xs leading-tight">{t(`categories.${cat.key}`)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Urgency */}
          <div>
            <label className="text-sm font-black uppercase tracking-wider text-muted-foreground block mb-3">
              {t('tender.urgency_label')}
            </label>
            <div className="grid grid-cols-3 gap-2.5">
              {([
                { key: 'urgent', emoji: '🔴' },
                { key: 'today', emoji: '🟠' },
                { key: 'flexible', emoji: '🟢' },
              ] as const).map(u => (
                <button
                  type="button"
                  key={u.key}
                  onClick={() => setUrgency(u.key)}
                  className={`py-3 px-2 rounded-2xl border-2 font-bold text-xs text-center transition-all flex flex-col items-center gap-1.5 ${
                    urgency === u.key
                      ? 'border-primary bg-primary/10 text-primary shadow-lg shadow-primary/10'
                      : 'border-border bg-card hover:border-primary/40 hover:bg-secondary'
                  }`}
                >
                  <span className="text-xl">{u.emoji}</span>
                  <span className="leading-tight">{t(`tender.urgency_${u.key}`)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="text-sm font-black uppercase tracking-wider text-muted-foreground block mb-3">
              {t('tender.description_label')}
            </label>
            <textarea
              required
              rows={5}
              placeholder={t('tender.description_placeholder')}
              value={description}
              onChange={e => setDescription(e.target.value)}
              className="w-full bg-card border-2 border-border rounded-2xl px-5 py-4 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all font-medium resize-none text-[15px] placeholder:text-muted-foreground/60"
            />
          </div>

          {/* Photo upload — key UX feature */}
          <div>
            <label className="text-sm font-black uppercase tracking-wider text-muted-foreground block mb-3">
              {t('tender.photos_label')}
            </label>
            <input ref={fileRef} type="file" multiple accept="image/*" capture="environment" onChange={handlePhotos} className="hidden" />
            <div className="grid grid-cols-4 gap-3">
              {photos.map((f, i) => (
                <div key={i} className="relative aspect-square bg-card border-2 border-border rounded-2xl overflow-hidden group">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all" />
                  <button type="button" onClick={() => removePhoto(i)}
                    className="absolute top-1.5 right-1.5 w-7 h-7 bg-black/70 rounded-full flex items-center justify-center text-white hover:bg-red-500 transition-colors opacity-0 group-hover:opacity-100">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {photos.length < 4 && user && (
                <button type="button" onClick={() => fileRef.current?.click()}
                  className="aspect-square bg-card border-2 border-dashed border-border rounded-2xl flex flex-col items-center justify-center text-muted-foreground hover:border-primary/60 hover:bg-primary/5 hover:text-primary transition-all group">
                  <Camera className="w-7 h-7 mb-1.5 group-hover:scale-110 transition-transform" />
                  <span className="text-xs font-bold">{t('tender.add_photo')}</span>
                  <span className="text-[10px] opacity-60 mt-0.5">{photos.length}/4</span>
                </button>
              )}
            </div>
            {user ? (
              <p className="text-xs text-muted-foreground mt-2">📸 {t('tender.photo_hint')}</p>
            ) : (
              // Attaching photos requires an account (Storage rules), so say it
              // upfront instead of letting the upload fail silently.
              <p className="text-xs text-muted-foreground mt-2 flex flex-wrap items-center gap-1.5">
                🔒 {t('tender.photo_login_hint')}
                <a href="/login" className="text-primary font-bold underline">{t('tender.photo_login_cta')}</a>
              </p>
            )}
          </div>

          {/* Budget + City */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-black uppercase tracking-wider text-muted-foreground block mb-3">
                {t('tender.budget_label')}
              </label>
              <input
                type="number"
                placeholder={t('tender.budget_placeholder')}
                value={budget}
                onChange={e => setBudget(e.target.value)}
                className="w-full bg-card border-2 border-border rounded-2xl px-4 py-3.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all font-medium"
              />
            </div>
            <div>
              <label className="text-sm font-black uppercase tracking-wider text-muted-foreground block mb-3">
                <MapPin className="inline w-3.5 h-3.5 mr-1" />{t('tender.city_label')}
              </label>
              <input
                type="text"
                placeholder={t('tender.city_placeholder')}
                value={city}
                onChange={e => setCity(e.target.value)}
                className="w-full bg-card border-2 border-border rounded-2xl px-4 py-3.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all font-medium"
              />
            </div>
          </div>

          {/* Contact info */}
          <div className="bg-linear-to-br from-card to-secondary/50 border-2 border-border rounded-2xl p-6 space-y-4">
            <p className="font-black uppercase tracking-wider text-muted-foreground text-sm">{t('tender.contacts_label')}</p>
            <input type="text" required placeholder={t('tender.name_placeholder')} value={clientName} onChange={e => setClientName(e.target.value)}
              className="w-full bg-background border-2 border-border rounded-xl px-4 py-3.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all font-medium" />
            <input type="tel" required placeholder={t('tender.phone_placeholder')} value={clientPhone} onChange={e => setClientPhone(e.target.value)}
              className="w-full bg-background border-2 border-border rounded-xl px-4 py-3.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all font-medium" />
          </div>

          {/* Submit */}
          <button type="submit" disabled={submitting}
            className="w-full bg-primary text-primary-foreground font-black py-5 rounded-2xl text-xl hover:bg-primary/90 transition-all hover:scale-[1.01] hover:shadow-2xl hover:shadow-primary/30 disabled:opacity-50 disabled:cursor-not-allowed">
            {submitting ? (
              <span className="flex items-center justify-center gap-3">
                <div className="w-5 h-5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                {t('tender.submitting')}
              </span>
            ) : (
              <span className="flex items-center justify-center gap-3">
                <Upload className="w-5 h-5" /> {t('tender.submit_btn')}
              </span>
            )}
          </button>
          <p className="text-center text-sm text-muted-foreground">{t('tender.footer_note')}</p>
        </form>
      </div>

      {/* Voice Assistant — hidden unless an admin enables it (system_config/voiceAssistant) */}
      {voiceEnabled && (
      <>
      {/* Voice Assistant FAB */}
      <button
        onClick={() => setVoiceOpen(true)}
        className="fixed bottom-6 right-6 z-40 bg-linear-to-r from-primary to-primary/80 text-primary-foreground shadow-2xl shadow-primary/30 rounded-full flex items-center gap-3 pl-5 pr-6 py-4 font-bold text-sm hover:scale-105 transition-all hover:shadow-primary/40 group"
      >
        <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center group-hover:bg-white/30 transition-colors">
          <Mic className="w-4 h-4" />
        </div>
        <div className="text-start">
          <div className="font-black text-sm">{t('voice.fab_label')}</div>
          <div className="text-[10px] opacity-80 font-medium">{t('voice.fab_subtitle')}</div>
        </div>
      </button>

      {/* Voice Assistant Modal */}
      <VoiceAssistant
        isOpen={voiceOpen}
        onClose={() => setVoiceOpen(false)}
        onTenderGenerated={handleVoiceTender}
      />
      </>
      )}
    </div>
  );
}
